/**
 * Admin Dashboard API Module
 * Abstraction layer for all dashboard data fetches. Provides request deduplication (_inflight Map),
 * batch prefetch caching (_batchCache, one-shot 5min TTL), and retry-with-backoff via core queue.
 * All endpoints go through fetchDashboardData() which returns {success, data, error?}.
 * Sanitizes GraphQL args to prevent injection; handles 429 Throttled + GraphQL Throttled responses.
 */

(function () {
  'use strict';

  const _isDev =
    window.location.hostname === 'localhost' || window.location.hostname.includes('myshopify.com');
  const _dbg = _isDev ? console.debug.bind(console) : () => {};
  const _err = _isDev ? console.error.bind(console) : () => {};

  // Sanitize strings interpolated into GraphQL query arguments.
  // Strips double-quotes and backslashes to prevent injection if values originate from user input.
  const sanitizeQueryStr = (s) => (s == null ? '' : String(s).replace(/["\\]/g, ''));

  window.AdminDashboardAPI = {
    // Reference to core module (injected during init)
    core: null,

    // In-flight request deduplication: same endpoint → same promise
    _inflight: new Map(),

    // Time-window coalescing: same endpoint requested within DEDUP_WINDOW_MS → share same in-flight.
    // Tracks the timestamp (ms) when each endpoint's in-flight entry was registered.
    // Entries are cleared when the in-flight promise settles, same as _inflight.
    _inflightStartTs: new Map(),

    // Maximum age (ms) of an in-flight entry that qualifies for dedup coalescing.
    // Requests arriving within this window share the same promise; later callers get a fresh fetch.
    _DEDUP_WINDOW_MS: 500,

    // Batch prefetch cache: endpoint name → {data, at} (populated by prefetchBatch)
    // Entries are consumed on first read (one-shot) OR expired after BATCH_CACHE_TTL_MS.
    // Without TTL, a prefetch at page-load can be served hours later as if it were fresh.
    _batchCache: new Map(),

    // How long a batch-prefetch entry is considered fresh (5 minutes).
    // After this, the one-shot entry is discarded and a live fetch is made instead.
    _BATCH_CACHE_TTL_MS: 5 * 60 * 1000,

    // ---------------------------------------------------------------------------
    // View-level abort registry.
    //
    // Maps a "view key" (e.g. tab id or period-selector id) to the AbortController
    // that owns all in-flight requests for that view. When the user switches tab or
    // changes a period selector, callers invoke cancelView(viewKey) to abort every
    // pending request for the previous view — preventing stale responses from
    // overwriting fresh data.
    //
    // Usage:
    //   const signal = AdminDashboardAPI.getSignalForView('tab-analytics');
    //   await AdminDashboardAPI.fetchDashboardData(endpoint, ctx, { signal });
    //
    //   // On tab switch:
    //   AdminDashboardAPI.cancelView('tab-analytics');
    // ---------------------------------------------------------------------------
    _viewControllers: new Map(),

    /**
     * Return a new AbortSignal for the given view key.
     * If a controller already exists for this view, it is replaced (the previous
     * controller is NOT aborted here — cancelView() does that explicitly).
     *
     * @param {string} viewKey - Stable string identifying the view (tab id, selector id, etc.)
     * @returns {AbortSignal}
     */
    getSignalForView(viewKey) {
      const controller = new AbortController();
      this._viewControllers.set(viewKey, controller);
      return controller.signal;
    },

    /**
     * Abort all in-flight requests for a view and remove its controller.
     * Safe to call even if no requests are active for that key.
     *
     * @param {string} viewKey - Same key used in getSignalForView()
     */
    cancelView(viewKey) {
      const controller = this._viewControllers.get(viewKey);
      if (controller) {
        controller.abort();
        this._viewControllers.delete(viewKey);
      }
    },

    /**
     * Abort every tracked view simultaneously. Useful on full dashboard reload.
     */
    cancelAllViews() {
      for (const controller of this._viewControllers.values()) {
        controller.abort();
      }
      this._viewControllers.clear();
    },

    /**
     * Initialize API module with core reference
     */
    init(coreModule) {
      this.core = coreModule;
    },

    /**
     * Generic dashboard data fetcher
     * Abstracts common fetch->parse->validate pattern used in 45+ functions
     *
     * @param {string} endpoint - API endpoint (with or without leading ?)
     * @param {string} errorContext - Context for error messages
     * @param {object} options - Additional options (pass force:true to bypass dedup)
     * @returns {Promise<{success: boolean, data: any, error?: any}>}
     */
    async fetchDashboardData(endpoint, errorContext = 'data', options = {}) {
      // Urgent requests skip dedup entirely — they must jump the queue immediately.
      // Reusing a non-urgent in-flight promise would negate the priority boost.
      if (!options.force && !options.urgent && this._inflight.has(endpoint)) {
        // Time-window coalescing: only share the in-flight promise if the request was
        // registered within the last DEDUP_WINDOW_MS. After that the in-flight entry is
        // still alive (waiting for the server) but a new caller gets a fresh fetch to
        // avoid returning stale in-progress data from a request that started seconds ago.
        const startTs = this._inflightStartTs.get(endpoint) || 0;
        if (Date.now() - startTs <= this._DEDUP_WINDOW_MS) {
          return this._inflight.get(endpoint);
        }
      }

      const promise = this._doFetch(endpoint, errorContext, options);
      // Only register non-urgent fetches for dedup (urgent ones are fire-and-forget).
      if (!options.force && !options.urgent) {
        this._inflight.set(endpoint, promise);
        this._inflightStartTs.set(endpoint, Date.now());
      }
      promise.finally(() => {
        this._inflight.delete(endpoint);
        this._inflightStartTs.delete(endpoint);
      });
      return promise;
    },

    // ---------------------------------------------------------------------------
    // Retry configuration for transient failures.
    // Only 5xx server errors and network-level failures are retried.
    // 4xx client errors are permanent and must not be retried.
    // ---------------------------------------------------------------------------

    /** Maximum number of retry attempts after an initial failure. */
    _RETRY_MAX: 3,

    /** Base delay in ms for exponential backoff: 1s, 2s, 4s. */
    _RETRY_BASE_DELAY_MS: 1000,

    /**
     * Classify whether an HTTP status code or thrown error is a transient failure
     * that warrants a retry.
     *
     * Retryable:   5xx server errors, network errors (no status), null/undefined status
     * Non-retryable: 4xx client errors (the request itself is broken; retrying won't help)
     *
     * @param {number|null|undefined} status - HTTP status code, or falsy for network errors
     * @returns {boolean}
     */
    _isRetryable(status) {
      if (!status) return true; // network-level error (fetch threw, no HTTP response)
      return status >= 500 && status < 600;
    },

    /**
     * Sleep for `ms` milliseconds. Exposed as a property so tests can substitute
     * a no-op to avoid real timer delays.
     */
    _sleep(ms) {
      return new Promise((resolve) => {
        setTimeout(resolve, ms);
      });
    },

    async _doFetch(endpoint, errorContext, options = {}) {
      try {
        // Check batch prefetch cache first (one-shot: consumed on read).
        // Only use the cache when the caller passes no extra parameters beyond the
        // endpoint name itself — e.g. "endpoint=today-stats".  If the caller adds
        // "&period=30d" or any other query param the batch was fetched with default
        // settings and would return wrong-period data, so skip the cache.
        const endpointName = this._extractEndpointName(endpoint);
        const hasExtraParams = /[?&](?!endpoint=)[^=]+=/.test(
          endpoint.startsWith('?') ? endpoint : `?${endpoint}`
        );
        if (endpointName && !hasExtraParams && this._batchCache.has(endpointName)) {
          const entry = this._batchCache.get(endpointName);
          this._batchCache.delete(endpointName); // always remove (one-shot)
          // Only use the cached value if it is still within the TTL window.
          // Stale entries (page open for hours without consuming the cache) fall
          // through to a live fetch so the user never sees hours-old data.
          if (Date.now() - entry.at <= this._BATCH_CACHE_TTL_MS) {
            return { success: true, data: entry.data };
          }
        }

        // Ensure endpoint starts with ?
        const formattedEndpoint = endpoint.startsWith('?') ? endpoint : `?${endpoint}`;

        // Add cache buster if requested
        if (!this.core) return { success: false, error: 'API module not initialized' };
        const cacheBuster =
          options.cacheBuster ||
          (this.core.config.cacheBuster ? `&_cb=${this.core.config.cacheBuster}` : '');
        const url = `${this.core.config.apiProxyUrl}${formattedEndpoint}${cacheBuster}`;

        // Propagate caller-supplied AbortSignal into every fetch attempt so that
        // view-level cancellation (tab/period switch) immediately stops the request.
        const callerSignal = options.signal || null;

        // Exponential backoff retry loop.
        // Attempt 0 is the original request; attempts 1..maxRetries are retries.
        // Only transient errors (5xx, network failures) trigger a retry.
        // 4xx client errors are returned immediately — retrying a bad request wastes quota.
        // Timeouts and explicit cancellations are NOT retried — they must surface immediately.
        const maxRetries = options.maxRetries != null ? options.maxRetries : this._RETRY_MAX;
        let lastResult = null;

        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          // If a caller-supplied signal was already aborted before we start an attempt,
          // bail immediately — there is nothing useful we can do with a cancelled request.
          if (callerSignal && callerSignal.aborted) {
            return { success: false, error: 'cancelled', cancelled: true };
          }

          if (attempt > 0) {
            // Exponential backoff: 1s, 2s, 4s, …  (capped at 30s to be safe)
            const delayMs = Math.min(this._RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1), 30000);
            _dbg(
              '[API] retry',
              attempt,
              '/',
              maxRetries,
              'for',
              endpointName || endpoint,
              'delay',
              delayMs + 'ms'
            );
            await this._sleep(delayMs);
          }

          try {
            // Use queued fetch to prevent throttling (urgent requests jump to front of queue)
            const _t0 = Date.now();
            const fetchOpts = callerSignal
              ? { ...(options.fetchOptions || {}), signal: callerSignal }
              : options.fetchOptions || {};
            const { response, data: result } = await this.core.queuedFetch(
              url,
              fetchOpts,
              !!options.urgent
            );
            const _dur = Date.now() - _t0;
            _dbg(
              '[API] fetch',
              endpointName || endpoint,
              response.status,
              _dur + 'ms',
              attempt > 0 ? `(retry ${attempt})` : ''
            );
            if (_isDev && _dur > 5000) {
              console.warn('[API] slow fetch (' + _dur + 'ms):', endpointName || endpoint);
            }
            // Response-size awareness: log payload size in dev mode so engineers can
            // spot oversized responses that contribute to slow dashboard loads on mobile.
            if (_isDev) {
              try {
                const _sizeBytes = new TextEncoder().encode(JSON.stringify(result)).length;
                _dbg(
                  '[API] response size',
                  endpointName || endpoint,
                  Math.round(_sizeBytes / 1024) + 'KB'
                );
                if (_sizeBytes > 200 * 1024) {
                  console.warn(
                    '[API] large response (' + Math.round(_sizeBytes / 1024) + 'KB > 200KB):',
                    endpointName || endpoint,
                    '— consider paginating or trimming fields'
                  );
                }
              } catch (_sizeErr) {
                // Size estimation is best-effort; never block the response path.
              }
            }

            if (response.ok) {
              if (result.success) {
                return { success: true, data: result.data };
              } else {
                // Application-level error — not retryable (server understood the request)
                return { success: false, error: result.error || 'API returned success:false' };
              }
            } else {
              // HTTP error response received
              lastResult = { success: false, error: `HTTP ${response.status}` };
              if (!this._isRetryable(response.status)) {
                // 4xx: client error, do not retry
                return lastResult;
              }
              // 5xx: fall through to retry on next iteration
            }
          } catch (fetchError) {
            // TimeoutError — do NOT retry. Surface immediately so the UI can show the
            // targeted "Request timed out — check your connection" message.
            const TimeoutError = window.AdminDashboardTimeoutError;
            if (TimeoutError && fetchError instanceof TimeoutError) {
              return { success: false, error: fetchError, timedOut: true };
            }
            // Explicit cancellation (tab/period switch) — do NOT retry.
            if (fetchError.name === 'AbortError') {
              return { success: false, error: fetchError, cancelled: true };
            }
            // Network-level error (fetch threw — no HTTP response at all)
            lastResult = { success: false, error: fetchError };
            // Always retryable — network blip, DNS hiccup, etc.
          }
        }

        // All attempts exhausted
        return lastResult || { success: false, error: 'Request failed after retries' };
      } catch (error) {
        return { success: false, error };
      }
    },

    /**
     * Extract the endpoint name from a query string like "endpoint=today-stats&period=7d"
     * Returns just "today-stats", or null if not parseable.
     */
    _extractEndpointName(endpoint) {
      const match = endpoint.match(/(?:^|[?&])endpoint=([a-z0-9-]+)/);
      return match ? match[1] : null;
    },

    /**
     * Fetch with DOM update helper
     * Fetches data and updates DOM elements if successful
     *
     * @param {string} endpoint - API endpoint
     * @param {string} errorContext - Context for errors
     * @param {function} updateFn - Function to call with successful data
     * @param {function} errorFn - Optional function to call on error
     */
    async fetchAndUpdate(endpoint, errorContext, updateFn, errorFn = null) {
      try {
        const result = await this.fetchDashboardData(endpoint, errorContext);

        if (result.success) {
          updateFn(result.data);
        } else if (errorFn) {
          errorFn(result.error);
        }

        return result;
      } catch (error) {
        if (errorFn) {
          errorFn(error.message || 'Unknown error');
        }
        return { success: false, error: error.message };
      }
    },

    /**
     * Parallel fetch helper
     * Fetches multiple endpoints in parallel using the request queue
     *
     * @param {Array<{endpoint: string, context: string}>} requests - Must be a non-empty array
     * @returns {Promise<Array<{success: boolean, data: *, context: string, endpoint: string}>>}
     */
    async fetchMultiple(requests) {
      if (!Array.isArray(requests) || requests.length === 0) {
        return [];
      }
      const promises = requests.map((req) => {
        if (!req || typeof req.endpoint !== 'string' || !req.endpoint) {
          return Promise.resolve({
            success: false,
            error: 'Invalid request entry',
            context: req?.context ?? '',
            endpoint: '',
          });
        }
        return this.fetchDashboardData(req.endpoint, req.context).then((result) => ({
          ...result,
          context: req.context,
          endpoint: req.endpoint,
        }));
      });
      return Promise.all(promises);
    },

    /**
     * Server-side batch fetch — combines up to 10 endpoints into one HTTP call.
     * The server executes all endpoints in parallel and returns combined results.
     *
     * @param {string[]} endpoints - Array of endpoint names (e.g. ['today-stats', 'live-visitors'])
     * @returns {Promise<Object>} Object keyed by endpoint name, each value is the data (or {error})
     */
    async fetchBatch(endpoints) {
      if (!this.core) return {};
      try {
        const url = `${this.core.config.apiProxyUrl}?endpoint=batch`;
        const { response, data } = await this.core.queuedFetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoints }),
        });
        if (response.ok && data.success) {
          return data.data || {};
        }
        return {};
      } catch (err) {
        _err('Batch fetch error:', err);
        return {};
      }
    },

    /**
     * Prefetch multiple endpoints via server-side batch, storing results in cache.
     * Subsequent fetchDashboardData calls for these endpoints will hit the cache
     * instead of making individual HTTP requests.
     *
     * @param {string[]} endpoints - Array of endpoint names (max 10 per batch)
     * @returns {Promise<void>}
     */
    async prefetchBatch(endpoints) {
      const results = await this.fetchBatch(endpoints);
      const now = Date.now();
      for (const [name, data] of Object.entries(results)) {
        if (data && !data.error) {
          // Store with a timestamp so the read path can enforce the TTL.
          this._batchCache.set(name, { data, at: now });
        }
      }
    },

    /**
     * Fetch with loading state management
     * Shows loading indicator, fetches data, updates DOM, clears loading.
     * On failure (after all retries in _doFetch), shows an error state with a
     * "Retry" button that re-triggers the entire load sequence when clicked.
     *
     * @param {string} endpoint - API endpoint
     * @param {string} errorContext - Context for errors
     * @param {string} containerId - Container to show loading in
     * @param {function} updateFn - Function to update DOM with data
     * @param {function|null} [retryFn=null] - Optional custom retry function. When null,
     *   the default retry re-calls fetchWithLoading with the same arguments.
     */
    async fetchWithLoading(endpoint, errorContext, containerId, updateFn, retryFn = null) {
      const container = document.getElementById(containerId);
      if (!container) {
        return { success: false, error: 'Container not found' };
      }

      window.AdminDashboardUI?.showLoading(container);

      try {
        const result = await this.fetchDashboardData(endpoint, errorContext);

        if (result.success) {
          // Notify connection health tracker of a successful request so it can
          // clear the consecutive-timeout counter and hide the problem banner.
          window.AdminDashboard?.recordFetchSuccess?.();
          updateFn(result.data, container);
        } else if (result.cancelled) {
          // Request was cancelled by a tab/period switch — do NOT show an error banner.
          // The new view will render its own content.
        } else {
          // Build a retry callback so the user can re-trigger the load without refreshing.
          const onRetry =
            retryFn ||
            (() => this.fetchWithLoading(endpoint, errorContext, containerId, updateFn, retryFn));
          // Show a distinct message for timeout vs generic network/server error.
          const userMsg = result.timedOut
            ? `Request timed out — check your connection`
            : `Failed to load ${errorContext}`;
          // Notify connection health tracker of a timeout so it can count toward
          // the 3-strike threshold that triggers connection-problem mode.
          if (result.timedOut) {
            window.AdminDashboard?.recordFetchTimeout?.();
          }
          window.AdminDashboardUI?.showError(container, userMsg, null, onRetry);
        }

        return result;
      } catch (error) {
        const onRetry =
          retryFn ||
          (() => this.fetchWithLoading(endpoint, errorContext, containerId, updateFn, retryFn));
        window.AdminDashboardUI?.showError(
          container,
          `Failed to load ${errorContext}`,
          null,
          onRetry
        );
        return { success: false, error: error.message };
      }
    },

    /**
     * Fetch Klaviyo campaign name from API
     * Specific helper for Klaviyo integration
     */
    async fetchKlaviyoCampaignName(campaignId) {
      try {
        const result = await this.fetchDashboardData(
          `endpoint=klaviyo-campaign&id=${campaignId}`,
          'Klaviyo campaign name'
        );

        if (result.success && result.data?.name) {
          const name = result.data.name;
          // Filter out "Pending" placeholder
          if (name && !name.includes('Pending')) {
            return name;
          }
        }

        return 'Campaign'; // Default fallback
      } catch (error) {
        _err('fetchKlaviyoCampaignName error:', error);
        return 'Campaign';
      }
    },

    /**
     * Generic GraphQL query executor
     * Used for Shopify Admin API queries
     */
    async executeGraphQLQuery(query, errorContext = 'GraphQL query') {
      try {
        const result = await this.fetchDashboardData('endpoint=graphql', errorContext, {
          fetchOptions: {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ query }),
          },
        });

        return result;
      } catch (error) {
        _err('executeGraphQLQuery error:', error);
        return { success: false, error };
      }
    },

    /**
     * Build GraphQL query for common operations
     * Factory for common query patterns
     */
    buildGraphQLQuery(queryType, variables = {}) {
      const queries = {
        orders: (vars) => `{
        orders(first: ${vars.limit || 250}, reverse: true${vars.query ? `, query: "${sanitizeQueryStr(vars.query)}"` : ''}${vars.after ? `, after: "${sanitizeQueryStr(vars.after)}"` : ''}) {
          edges {
            node {
              id
              name
              ${vars.fields || 'createdAt totalPriceSet { shopMoney { amount } }'}
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }`,

        products: (vars) => `{
        products(first: ${vars.limit || 100}${vars.query ? `, query: "${sanitizeQueryStr(vars.query)}"` : ''}) {
          edges {
            node {
              id
              title
              ${vars.fields || 'variants(first: 10) { edges { node { id price inventoryQuantity } } }'}
            }
          }
        }
      }`,

        customers: (vars) => `{
        customers(first: ${vars.limit || 100}${vars.query ? `, query: "${sanitizeQueryStr(vars.query)}"` : ''}) {
          edges {
            node {
              id
              email
              ${vars.fields || 'firstName lastName ordersCount totalSpent'}
            }
          }
        }
      }`,
      };

      const queryFn = queries[queryType];
      if (!queryFn) {
        throw new Error(`Unknown query type: ${queryType}`);
      }

      return queryFn(variables);
    },

    /**
     * Batch fetch helper for pagination
     * Automatically handles GraphQL pagination
     *
     * @param {function(Object): string} queryBuilder - Function that receives variables and returns a GraphQL query string
     * @param {Object} [variables={}] - Variables to pass to each query
     * @param {number} [maxPages=10] - Maximum number of pages to fetch (must be a positive integer)
     * @returns {Promise<Array<Object>>} Accumulated node objects across all pages
     */
    async fetchAllPages(queryBuilder, variables = {}, maxPages = 10) {
      if (typeof queryBuilder !== 'function') {
        return [];
      }
      const pageLimit = Number.isFinite(maxPages) && maxPages > 0 ? Math.floor(maxPages) : 10;
      const allData = [];
      let hasNextPage = true;
      let cursor = null;
      let page = 0;

      try {
        while (hasNextPage && page < pageLimit) {
          const vars = { ...variables, after: cursor };
          const query = queryBuilder(vars);
          const result = await this.executeGraphQLQuery(query, `Batch fetch page ${page + 1}`);

          if (!result.success) break;

          // Extract data and pagination info (structure depends on query)
          const data = result.data;
          if (data?.edges) {
            allData.push(...data.edges.map((e) => e.node));
            hasNextPage = data.pageInfo?.hasNextPage || false;
            cursor = data.pageInfo?.endCursor || null;
          } else {
            break;
          }

          page++;
        }

        return allData;
      } catch (error) {
        _err('fetchAllPages error (returning partial data):', error);
        return allData; // Return partial data if any was fetched
      }
    },

    /**
     * Error handler with retry suggestion
     * Provides user-friendly error messages
     */
    handleAPIError(error, context, containerId = null) {
      const errorMessages = {
        Throttled: 'API rate limit reached. Please wait a moment and try again.',
        'HTTP 429': 'Too many requests. Please wait a moment.',
        'HTTP 401': 'Authentication failed. Please check API credentials.',
        'HTTP 403': 'Access denied. Please check permissions.',
        'HTTP 404': 'Endpoint not found.',
        'HTTP 500': 'Server error. Please try again later.',
        NetworkError: 'Network connection failed. Please check your internet connection.',
      };

      const errorType = typeof error === 'string' ? error : error?.message || 'Unknown';
      const userMessage = errorMessages[errorType] || `Failed to load ${context}`;

      if (containerId) {
        window.AdminDashboardUI?.showError(containerId, userMessage);
      }

      return { success: false, error: userMessage };
    },

    /**
     * Cache helper for expensive API calls
     * Simple in-memory cache with TTL
     */
    cache: new Map(),

    /**
     * Retrieve a cached value, returning null if missing or expired.
     *
     * @param {string} key - Cache key
     * @returns {*} Cached data or null
     */
    cacheGet(key) {
      if (typeof key !== 'string' || !key) return null;
      const item = this.cache.get(key);
      if (!item) return null;

      // Check if expired
      if (Date.now() > item.expires) {
        this.cache.delete(key);
        return null;
      }

      return item.data;
    },

    /**
     * Store a value in the cache with a TTL.
     *
     * @param {string} key - Cache key
     * @param {*} data - Data to cache
     * @param {number} [ttlMinutes=5] - Time to live in minutes (must be a positive number)
     */
    cacheSet(key, data, ttlMinutes = 5) {
      if (typeof key !== 'string' || !key) return;
      // Guard ttlMinutes: must be a finite positive number; default to 5 if invalid
      const ttl = Number.isFinite(ttlMinutes) && ttlMinutes > 0 ? ttlMinutes : 5;
      // Evict oldest entry if cache exceeds 400 keys to prevent unbounded growth
      if (this.cache.size >= 400) {
        const oldestKey = this.cache.keys().next().value;
        this.cache.delete(oldestKey);
      }
      this.cache.set(key, {
        data,
        expires: Date.now() + ttl * 60 * 1000,
      });
    },
    cacheClear(key = null) {
      if (key) {
        this.cache.delete(key);
      } else {
        this.cache.clear();
      }
    },

    /**
     * Cached fetch wrapper
     * Fetches from cache if available, otherwise from API
     */
    async fetchCached(endpoint, errorContext, ttlMinutes = 5) {
      const cacheKey = endpoint;
      const cached = this.cacheGet(cacheKey);

      if (cached) {
        return { success: true, data: cached, fromCache: true };
      }

      const result = await this.fetchDashboardData(endpoint, errorContext);

      if (result.success) {
        this.cacheSet(cacheKey, result.data, ttlMinutes);
      }

      return { ...result, fromCache: false };
    },

    /**
     * Stale-while-revalidate fetch.
     *
     * On first call for a key: fetches live, persists to sessionStorage, returns data.
     * On subsequent calls (same session, within SWR_FRESH_TTL_MS):
     *   1. Immediately calls updateFn with the cached data (so UI paints instantly).
     *   2. Silently re-fetches in the background and calls updateFn again with fresh data.
     * On stale hit (beyond TTL): shows cached data immediately, then refreshes.
     *
     * This ensures users on slow connections see data right away on revisit instead
     * of staring at spinners for the full RTT.
     *
     * @param {string} swrKey - Stable logical key (e.g. 'today-stats')
     * @param {string} endpoint - API endpoint passed to fetchDashboardData
     * @param {string} errorContext - Error label
     * @param {function(data: any, fromCache: boolean): void} updateFn - DOM update callback
     * @param {object} [options] - Passed through to fetchDashboardData (signal, urgent, etc.)
     * @returns {Promise<{success: boolean, data?: any, fromCache?: boolean, error?: any}>}
     */
    async fetchWithStaleRevalidate(swrKey, endpoint, errorContext, updateFn, options = {}) {
      const core = this.core;
      const cached = core ? core.swrRead(swrKey) : null;

      if (cached) {
        // Show cached data immediately — user gets instant feedback
        try {
          updateFn(cached.data, true);
        } catch (_e) {
          // DOM update error must not block the revalidation fetch
        }
        // Silently revalidate in the background (fire-and-forget from caller's perspective)
        this.fetchDashboardData(endpoint, errorContext, options)
          .then((result) => {
            if (result.success) {
              if (core) core.swrWrite(swrKey, result.data);
              try {
                updateFn(result.data, false);
              } catch (_e) {
                /* DOM update error — ignore */
              }
            }
          })
          .catch(() => {
            /* background revalidation failure is silent — stale data already shown */
          });
        return { success: true, data: cached.data, fromCache: true };
      }

      // No cached data — fetch live and persist on success
      const result = await this.fetchDashboardData(endpoint, errorContext, options);
      if (result.success) {
        if (core) core.swrWrite(swrKey, result.data);
        try {
          updateFn(result.data, false);
        } catch (_e) {
          /* DOM update error */
        }
      }
      return { ...result, fromCache: false };
    },
  };
})();
